import React, { useEffect, useRef, useState } from "react";

/**
 * Animates a numeric value from 0 → target when the element enters viewport.
 * Uses IntersectionObserver + rAF; runs once.
 */
export function Counter({ value = 100, suffix = "", duration = 1400 }) {
  const ref = useRef(null);
  const [current, setCurrent] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setCurrent(Math.round(eased * value));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref}>
      {current.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function StatsBar({ items }) {
  return (
    <section className="relative bg-white">
      <div className="container-brand grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 py-10 md:py-14">
        {items.map((it) => (
          <div key={it.label} className="text-center md:text-left">
            <div className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight">
              <Counter value={it.value} suffix={it.suffix || ""} />
            </div>
            <div className="mt-1 text-sm text-brand-textSoft font-medium">
              {it.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
